import { Component, Input, ElementRef, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy, SimpleChanges, Output, EventEmitter, Inject } from '@angular/core';
import { PushMessageSelection } from '../../providers/pushMessage/pushMessageSelection';
import { UIElement, UIGroup, UIPopup } from '../../models';
import { RegularPopupComponent, BackdropComponent } from '../../../../shared/components';
import { ManagedSubject, ManagedSubscription } from '../../../../shared';
import * as Immutable from "immutable";
import { PushMessageStore } from '../../providers/pushMessage';
import { BaseComponent } from '../../baseComponent';
import { ImageSets } from '../../../../shared/utils';

@Component({
  selector: 'push-message',
  templateUrl: './pushMessageComponent.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PushMessageComponent extends BaseComponent {

  @ViewChild(BackdropComponent) protected backdropComponent: BackdropComponent;

  @Input()
  public uiPopup: UIPopup;

  @Input()
  public sessionId: number;


  // Stores the active session ID. When the user interacts with the push message UI, another session may be processed in between.  
  // Until we support multiple push message popups, we must save the active session ID here to prevent the popup content 
  // from being removed due to a minor, non-popup-related task in a new session.  
  // Note: The push message popup should use the same session ID, but this can be easily overlooked by C# developers.  
  public activeSessionId: number;

  // See notes about activeSessionId.
  public activeUIPopup: UIPopup;

  @Output()
  action = new EventEmitter<PushMessageSelection>();

  @ViewChild(RegularPopupComponent)
  private _popup: RegularPopupComponent;

  public visible = false;

  public title = "";

  public icon = "";

  public width: string;

  public height: string;

  public actions = [];

  public leadParagraph = "";

  public paragraph = "";

  public cssClass = "";

  public modal = true;

  public uiElement: UIElement;

  public uiElementId: number;

  public imageSet: string;

  public elementSubscription: ManagedSubscription;

  constructor(
    @Inject(PushMessageStore) public pushMessageStore: PushMessageStore,
    public cd: ChangeDetectorRef
  ) {

    super();

    if (this.backdropComponent)
      this.backdropComponent.add(this._popup);
  }

  ngOnInit() {
    this.imageSet = this.getImageSet(ImageSets.PopupHeader);
    super.ngOnInit();

    this.listenChanges();
  }

  ngOnChanges(changes: SimpleChanges) {

    if (this.sessionId && this.uiPopup) {
      this.activeSessionId = this.sessionId;
      this.activeUIPopup = this.uiPopup;

      this.title = this.activeUIPopup.header;
      this.icon = this.activeUIPopup.icon;
      this.leadParagraph = this.activeUIPopup.leadParagraph;
      this.paragraph = this.activeUIPopup.paragraph;
      this.cssClass = this.activeUIPopup.cssClass;
      this.modal = this.activeUIPopup.modal;

      if (this.activeUIPopup.uiElements) {
        this.uiElement = this.pushMessageStore.getElement(this.activeUIPopup.uiElements.first(), this.activeSessionId);

        if (!this.uiElement)
          return;

        this.uiElementId = this.uiElement.renderId;
        this.cd.markForCheck();
      }

      if (this.activeUIPopup.width > 0)
        this.width = this.activeUIPopup.width + "px";
      else
        this.width = null;

      if (this.activeUIPopup.height > 0)
        this.height = this.activeUIPopup.height + "px";
      else
        this.height = null;

      this.visible = true;

      this.listenChanges();
    }
  }

  public show(): void {

    if (this.modal) {
      this.backdropComponent.add(this.popup);
      this.backdropComponent.visible = true;
      this.backdropComponent.updateBackDropStyle();
    }

    setTimeout(() => {
      this.popup.open();
      this.cd.detectChanges();
    }, 0);
  }

  public close(): void {
    this.popup.close();
    this.visible = false;
    this.activeSessionId = null;
    this.activeUIPopup = null;
    this.cd.detectChanges();
  }

  public get popup(): RegularPopupComponent {
    let popupComponent = this._popup;
    if (popupComponent == null) {
      this.visible = true;
      this.cd.detectChanges();
      popupComponent = this._popup;
    }
    return popupComponent;
  }

  public onAction(event: PushMessageSelection) {
    this.action.emit(event);
  }

  public getCssClass(uiElementId2: number) {

    let uiElement2 = this.pushMessageStore.getElement(uiElementId2, this.activeSessionId);

    if (!uiElement2)
      return "";

    // Do not effect buttons.
    if (uiElement2.className == "UIInput") {
      return "";
    }

    // Do not effect UIGroup rendered with a div.
    if (uiElement2.className == "UIGroup") {
      let uiGroup = uiElement2 as UIGroup;
      if (uiGroup.includeDiv) {
        return "";
      }
    }

    return uiElement2.cssClass;
  }

  public listenChanges() {

    this.unsubscribe();

    this.elementSubscription = this.pushMessageStore.onElementChange(this.activeSessionId, this.uiElementId, (uiElement: UIElement): void => {

      this.uiElement = uiElement;
      this.uiElementId = uiElement.renderId;

      this.cd.markForCheck();

    }).unsubscribeOn(this.unsubscribeSubject);
  }

  ngOnDestroy() {
    this.unsubscribe();

    super.ngOnDestroy();
  }

  unsubscribe() {
    if (this.elementSubscription)
      this.elementSubscription.unsubscribe();
  }
}